import { useParams, Link } from 'react-router-dom';
import { Heart, Calendar, MapPin, ArrowLeft, Quote } from 'lucide-react';
import { ImageGallery } from '../components/ImageGallery';
import { memories } from '../data/memories';

export const MemoryDetailPage = () => {
  const { slug } = useParams();
  const memory = memories.find((m) => m.slug === slug);

  if (!memory) {
    return (
      <div className="min-h-screen pt-20 bg-[#0F0505] flex items-center justify-center" data-testid="memory-not-found">
        <div className="text-center">
          <h1 className="font-['Playfair_Display'] text-3xl text-[#F5F5F0] mb-4">Memory Not Found</h1>
          <Link to="/memories" className="text-[#D4AF37] hover:underline">
            Return to Memories
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-[#0F0505]" data-testid={`memory-detail-${slug}`}>
      {/* Hero Section */}
      <section className="relative h-[60vh] md:h-[70vh]">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src={memory.heroImage}
            alt={memory.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F0505] via-[#0F0505]/60 to-transparent" />
        </div>

        {/* Back Button */}
        <div className="absolute top-24 left-4 md:left-8 z-10">
          <Link
            to="/memories"
            className="flex items-center gap-2 text-[#F5F5F0] hover:text-[#D4AF37] transition-colors glass px-4 py-2 rounded-full"
            data-testid="back-to-memories"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Memories</span>
          </Link>
        </div>

        {/* Content Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16">
          <div className="max-w-4xl mx-auto">
            {/* Subtitle */}
            <p className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-4">
              {memory.subtitle}
            </p>

            {/* Title */}
            <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl lg:text-6xl text-[#F5F5F0] mb-6">
              {memory.title}
            </h1>

            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-6 text-[#A18E90]">
              <span className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#D4AF37]" />
                {memory.date}
              </span>
              <span className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#D4AF37]" />
                {memory.location}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Description Section */}
      <section className="py-16 md:py-24 bg-[#0F0505]" data-testid="memory-description">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <Heart className="w-8 h-8 text-[#D4AF37] mx-auto mb-6" fill="#D4AF37" />
            <div className="w-16 h-0.5 bg-[#D4AF37] mx-auto" />
          </div>

          {/* Description Text */}
          <div className="space-y-6">
            {memory.description.split('\n\n').map((paragraph, index) => (
              <p key={index} className="text-[#A18E90] text-lg leading-relaxed text-center">
                {paragraph}
              </p>
            ))}
          </div>

          {/* Courses (if applicable) */}
          {memory.courses && (
            <div className="mt-12 p-8 glass rounded-xl">
              <h3 className="font-['Playfair_Display'] text-xl text-[#F5F5F0] mb-6 text-center">
                The {memory.courses.length} Courses
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {memory.courses.map((course, index) => (
                  <div key={index} className="text-center p-4 border border-[#D4AF37]/20 rounded-lg">
                    <span className="text-[#D4AF37] text-xs tracking-widest">Course {index + 1}</span>
                    <p className="text-[#F5F5F0] mt-2">{course}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Personal Note Section */}
      <section className="py-16 md:py-24 bg-[#1A0F0F]/50" data-testid="personal-note">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div className="glass p-8 md:p-12 rounded-xl relative">
            <Quote className="w-10 h-10 text-[#D4AF37]/30 absolute top-6 left-6" />
            <div className="relative z-10 text-center">
              <h3 className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-6">
                Why This Memory Matters
              </h3>
              <p className="font-['Cormorant_Garamond'] text-xl md:text-2xl text-[#F5F5F0] italic leading-relaxed">
                {memory.personalNote}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Photo Gallery Section */}
      <section className="py-16 md:py-24 bg-[#0F0505]" data-testid="photo-gallery">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-4">Gallery</p>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#F5F5F0]">
              Captured Moments
            </h2>
          </div>

          <ImageGallery images={memory.images} />
        </div>
      </section>

      {/* Navigation to Other Memories */}
      <section className="py-16 md:py-24 bg-[#1A0F0F]/50">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <p className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-6">Continue Exploring</p>
          <Link
            to="/memories"
            className="btn-primary inline-block"
            data-testid="explore-more-memories"
          >
            View All Memories
          </Link>
        </div>
      </section>
    </div>
  );
};
